import { AppLock } from '@/components/AppLock'
import { BrandSplash } from '@/components/BrandSplash'
import { JsStack } from '@/components/JSStack'
import toastConfig from '@/config/toast/toast-config'
import { colors } from '@/constants/colors'
import useOTAUpdates from '@/hooks/useOTAUpdates'
import { initSentry } from '@/lib/sentry'
import Providers from '@/providers'
import heapService from '@/services/heap'
import { useAuthStore, useStore } from '@/store'
import { useSessionStore } from '@/store'
import { PortalHost } from '@/ui/primitives/portal'
import { TransitionPresets } from '@react-navigation/stack'
import * as Sentry from '@sentry/react-native'
import { getUnixTime } from 'date-fns'
import Constants, { ExecutionEnvironment } from 'expo-constants'
import { useFonts } from 'expo-font'
import { Stack, useNavigationContainerRef } from 'expo-router'
import * as SplashScreen from 'expo-splash-screen'
import { useEffect, useState } from 'react'
import { View } from 'react-native'
import Toast from 'react-native-toast-message'
import { firebaseAuth } from '#/firebase'
import '#/global.css'
SplashScreen.preventAutoHideAsync()

/** A cold deep link into a card (a visit, a form) gets the tabs beneath it, so back lands home. */
export const unstable_settings = {
	initialRouteName: '(tabs)',
}

const navigationIntegration = Sentry.reactNavigationIntegration({
	enableTimeToInitialDisplay:
		Constants.executionEnvironment === ExecutionEnvironment.StoreClient,
})

initSentry({ integrations: [navigationIntegration] })

function RootLayout() {
	const [isAuthReady, setIsAuthReady] = useState(false)
	const [showBrandSplash, setShowBrandSplash] = useState(true)
	const ref = useNavigationContainerRef()
	const { setLastOpenedAt } = useSessionStore()
	const hasHydrated = useStore((state) => state._hasHydratedFromStorage)
	const { authFromFirebaseUser, signOut } = useAuthStore()
	useOTAUpdates()

	// Two faces, as the brand has them: PP Right Grotesk for headings, Maison Neue for everything
	// else. Keys must match tailwind.config.js `fontFamily` exactly; web uses them as the CSS name.
	const [fontsLoaded] = useFonts({
		'PPRightGrotesk-Medium': require('#/assets/fonts/PPRightGrotesk-Medium.otf'),
		'MaisonNeue-Book': require('#/assets/fonts/MaisonNeue-Book.otf'),
		'MaisonNeue-Medium': require('#/assets/fonts/MaisonNeue-Medium.otf'),
		'MaisonNeue-Bold': require('#/assets/fonts/MaisonNeue-Bold.otf'),
	})

	const onAuthStateReady = async () => {
		if (isAuthReady) {
			return
		}

		if (firebaseAuth.currentUser) {
			const authToken = await firebaseAuth.currentUser.getIdToken()
			const tokenResult = await firebaseAuth.currentUser.getIdTokenResult()

			authFromFirebaseUser(authToken, firebaseAuth.currentUser, tokenResult.claims)
			setIsAuthReady(true)
		} else {
			signOut()
			setIsAuthReady(true)
		}
	}

	useEffect(() => {
		if (ref) {
			navigationIntegration.registerNavigationContainer(ref)
		}
	}, [ref])

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		firebaseAuth.authStateReady().then(onAuthStateReady)
		heapService.bootstrap()
	}, [])

	useEffect(() => {
		if (hasHydrated) {
			setLastOpenedAt(getUnixTime(Date.now()))
		}
	}, [hasHydrated, setLastOpenedAt])

	useEffect(() => {
		if (hasHydrated && isAuthReady && fontsLoaded) {
			SplashScreen.hideAsync()
		}
	}, [hasHydrated, isAuthReady, fontsLoaded])

	if (!hasHydrated || !isAuthReady || !fontsLoaded) {
		return null
	}

	return (
		<>
			<AppLock>
				<RootLayoutNav />
			</AppLock>
			{/* Covers the app while it settles; the native splash hides under it, not before it. */}
			{showBrandSplash && <BrandSplash onDone={() => setShowBrandSplash(false)} />}
		</>
	)
}

function RootLayoutNav() {
	return (
		<Providers>
			{/* The ground behind every card and transition, so nothing ever shows the OS black. */}
			<View className="flex-1 bg-ground">
				<JsStack
					screenOptions={{
						// One push transition on both platforms, rather than each one's default.
						...TransitionPresets.SlideFromRightIOS,
						headerShown: false,
						cardStyle: { backgroundColor: colors.ground },
					}}
				>
					<Stack.Screen name="login" />
					<Stack.Screen name="verify-login" />
					<JsStack.Screen
						name="join"
						options={{
							...TransitionPresets.ModalPresentationIOS,
							headerShown: false,
							presentation: 'modal',
							gestureEnabled: true,
						}}
					/>
					<Stack.Screen name="(tabs)" />
					<Stack.Screen name="visits/[id]" />
					<Stack.Screen name="score" />
					<Stack.Screen name="whitening" />
					<Stack.Screen
						name="schedule"
						options={{ presentation: 'fullScreenModal', gestureEnabled: false }}
					/>
					<Stack.Screen name="support" />
					<Stack.Screen name="forms/completed/[id]" />
					<Stack.Screen
						name="forms/[id]"
						options={{ headerShown: false, gestureEnabled: false }}
					/>
				</JsStack>
			</View>
			<Toast
				config={toastConfig}
				position="bottom"
				bottomOffset={40}
				keyboardOffset={20}
				// Up on a soft spring, straight back down: the toast arrives with a little weight
				// and leaves without asking to be watched.
				animationConfig={{
					enter: { type: 'spring', friction: 9, tension: 70 },
					exit: { type: 'timing', duration: 220 },
				}}
			/>
			<PortalHost />
		</Providers>
	)
}

export default Sentry.wrap(RootLayout)
